import { z } from "zod/v4";
import { KeyValuePairSchema } from "../../client/keyvaluepair.js";
import { AddressInputSchema, AddressInputBackofficeSchema, AddressSchema, AddressBackofficeSchema, AddressInputOrganizationSchema, AddressOrganizationSchema, } from "./address-schema.js";
import { CustomerType } from "../constants.js";
import { OptionalEmailSchema } from "./email-schema.js";
import { OptionalBirthDateSchema, RequiredBirthDateSchema, } from "./birth-date-schema.js";
const CreateCustomerShared = {
    type: z.enum(CustomerType),
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    email: OptionalEmailSchema,
    externalReferences: z.array(KeyValuePairSchema).nullable().optional(),
    meta: z.array(KeyValuePairSchema).optional(),
};
export const CreateCustomerInputSchema = z.strictObject({
    ...CreateCustomerShared,
    birthDate: RequiredBirthDateSchema,
    phone: z.string().min(1),
    addresses: z.strictObject({
        delivery: AddressInputSchema,
        billing: AddressInputSchema,
    }),
    companyName: z.string().optional(),
});
export const CreateCustomerBackofficeInputSchema = z.strictObject({
    ...CreateCustomerShared,
    birthDate: OptionalBirthDateSchema,
    phone: z.string().min(1).optional(),
    addresses: z.strictObject({
        delivery: AddressInputBackofficeSchema,
        billing: AddressInputBackofficeSchema,
    }),
});
export const CustomerParentInputSchema = z.object({
    identifier: z.string().min(1),
    type: z.enum(["customer", "customerGroup"]),
});
export const CreateOrganizationCustomerInputSchema = z.strictObject({
    type: z.enum(CustomerType),
    identifier: z.string().min(1),
    companyName: z.string().min(1),
    email: OptionalEmailSchema,
    parents: z.array(CustomerParentInputSchema),
    externalReferences: z.array(KeyValuePairSchema).nullable().optional(),
    meta: z.array(KeyValuePairSchema).optional(),
    addresses: z.strictObject({
        delivery: AddressInputOrganizationSchema,
        billing: AddressInputOrganizationSchema,
    }),
});
export const UpdateCustomerInputSchema = z.strictObject({
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    birthDate: OptionalBirthDateSchema,
    email: OptionalEmailSchema,
    phone: z.string().min(1),
    addresses: z
        .strictObject({
        delivery: AddressInputSchema.optional(),
        billing: AddressInputSchema.optional(),
    })
        .optional(),
    externalReferences: z.array(KeyValuePairSchema).nullable().optional(),
    meta: z.array(KeyValuePairSchema).optional(),
});
export const UpdateCustomerInputBackofficeSchema = UpdateCustomerInputSchema.extend({
    phone: z.string().optional(),
    birthDate: OptionalBirthDateSchema,
    addresses: z
        .strictObject({
        delivery: AddressInputBackofficeSchema.optional(),
        billing: AddressInputBackofficeSchema.optional(),
    })
        .optional(),
    parents: z
        .array(z.strictObject({
        identifier: z.string(),
        type: z.literal("customerGroup"),
    }))
        .optional(),
});
export const UpdateOrganizationCustomerInputSchema = UpdateCustomerInputSchema.omit({
    firstName: true,
    lastName: true,
}).extend({
    companyName: z.string().min(1).optional(),
});
export const CustomerSchema = z.object({
    type: z.literal(CustomerType.INDIVIDUAL),
    identifier: z.string().min(1),
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    birthDate: RequiredBirthDateSchema,
    email: OptionalEmailSchema,
    phone: z.string().min(1), // required
    addresses: z.array(AddressSchema),
    externalReferences: z.array(KeyValuePairSchema).nullable().optional(),
    meta: z.array(KeyValuePairSchema).nullable().optional(),
});
// We need a organizationCustomerSchema without first and lastname, it cannot be there and with companyName
export const OrganizationCustomerSchema = CustomerSchema.omit({
    firstName: true,
    lastName: true,
    birthDate: true,
}).extend({
    type: z.literal(CustomerType.ORGANIZATION),
    companyName: z.string().min(1),
    addresses: z.array(AddressOrganizationSchema),
    phone: z.string().min(1).nullable(), // can be null for organization customers
});
export const OrganizationOrPrivatePersonCustomerSchema = z.discriminatedUnion("type", [CustomerSchema, OrganizationCustomerSchema]);
export const CustomerBackofficeSchema = z.object({
    type: z.literal(CustomerType.INDIVIDUAL),
    identifier: z.string().min(1),
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    birthDate: OptionalBirthDateSchema,
    email: OptionalEmailSchema,
    phone: z.string().min(1).nullable().optional(), // null from Crystallize when not set
    addresses: z.array(AddressBackofficeSchema),
    externalReferences: z.array(KeyValuePairSchema).nullable().optional(),
    meta: z.array(KeyValuePairSchema).nullable().optional(),
});
